import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import AccountInfo from "./AccountInfo";
import PersonalInfo from "./PersonalInfo";
import {
  getUser,
  getProfile,
  updateAccount,
  updateProfile,
  updatePhoto,
} from "../../services/authService";
import { AccountPayload, ProfilePayload } from "../../types";
import { setSecureItem } from "../../utils/LocalStorageManager";
import { useUser } from "../../contexts/UserContext";

export default function Profile() {
  const { setUser } = useUser();
  const [account, setAccount] = useState<any>(null);
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();

  const [formAccount, setFormAccount] = useState<AccountPayload>({
    name: "",
    email: "",
    mobile_no: "",
    change_password: false,
    current_password: undefined,
    password: undefined,
    password_confirmation: undefined,
  });

  const [formProfile, setFormProfile] = useState<ProfilePayload>({
    user_id: 0,
    full_name: "",
    gender: "",
    dob: "",
    contact_address: "",
    emergency_contact_person: "",
    emergency_contact_number: "",
    emergency_contact_address: "",
  });

  const [formPhoto, setFormPhoto] = useState<File | null>(null);

  // Fetch profile on load
  useEffect(() => {
    getUser().then((res) => {
      const accountData = res?.data;
      setAccount(accountData);
      setFormAccount(accountData || {});
    }).catch((err) => {
      console.error("Failed to load user account info:", err);
    });

    getProfile()
      .then((res) => {
        const profileData = res?.data;
        setProfile(profileData);
        setFormProfile(profileData || {});
      })
      .finally(() => setLoading(false));
  }, []);

  const handleCancelPhoto = useCallback(() => {
    setFormPhoto(null);
  }, []);

  // Save Account Info
  const handleSaveAccount = async (payload: Partial<AccountPayload>) => {
    const updatedAccount = { ...formAccount, ...payload };
    await updateAccount(updatedAccount);
    setAccount((prev: any) => ({ ...prev, ...updatedAccount }));
    setFormAccount(updatedAccount);
  };

  // Save Profile Info
  const handleSaveProfile = async (payload: Partial<ProfilePayload>) => {
    const updatedProfile = { ...formProfile, ...payload };
    await updateProfile(updatedProfile);
    setProfile((prev: any) => ({ ...prev, ...updatedProfile }));
    setFormProfile(updatedProfile);
  };

  // Save Photo
  const handleSavePhoto = async () => {
    if (!formPhoto || !profile?.user_id) return;

    const formData = new FormData();
    formData.append("user_id", profile.user_id);
    formData.append("photo", formPhoto);

    try {
      const newUserData = await updatePhoto(formData);
      
      // Get the freshly uploaded photo URL from the backend response
      const updatedPhotoUrl = newUserData?.data?.profile?.photo_url;

      // Update local state for the profile page layout
      setProfile((prev: any) => ({
        ...prev,
        photo_url: updatedPhotoUrl,
      }));

      // INSTANT HEADER SYNC: Update the global user context state
      // This forces Header.tsx to immediately render the new image
      if (updatedPhotoUrl) {
        setUser((prevUser: any) => {
          if (!prevUser) return null;
          return {
            ...prevUser,
            avatar_url: updatedPhotoUrl // Explicitly updates user.avatar_url used by Header
          };
        });
      }

      setFormPhoto(null);
    } catch (error) {
      console.error("Photo update failed:", error);
    }
  };

  if (loading) {
    return <div className="container py-5">Loading...</div>;
  }

  const handleAddBusiness = () => {
    navigate("/add-business");
  };

  return (
    <>
    <h1 className="page-header">My Profile</h1>
    <hr className="section-devider" />
    <div className="row">
        <div className="col-12">
          <div className="row">
            {/* Left section: Personal info, Photo, Account info */}
            <div className="col-md-8 mb-3">
              <div className="row">
                <div className="col-12 mb-4">
                  <PersonalInfo
                    data={profile}
                    formProfile={formProfile}
                    setFormProfile={setFormProfile}
                    onSave={handleSaveProfile}
                  />
                </div>

                <div className="col-12">
                  <div className="row">
                    {/* Profile Photo */}
                    <div className="col-sm-4 col-md-5 col-lg-4 mb-3">
                      <h4 className="text-secondary d-flex justify-content-between align-items-center">
                        <span>Profile Photo</span>
                        <span className="edit-icon" style={{ cursor: "pointer" }}>
                          {!formPhoto ? (
                            <i
                              className="bi bi-pencil-square"
                              onClick={() =>
                                document.getElementById("fileInput")?.click()
                              }
                            />
                          ) : (
                            <i
                              className="bi bi-x-square text-danger"
                              onClick={handleCancelPhoto}
                            />
                          )}
                        </span>
                      </h4>

                      <div className="photo-container text-center mb-3">
                        <img
                          src={
                            formPhoto
                              ? URL.createObjectURL(formPhoto)
                              : profile?.photo_url || "user-photo-bg.jpg"
                          }
                          alt="profile"
                          className="photo img-fluid rounded"
                        />

                        <input
                          type="file"
                          id="fileInput"
                          accept="image/*"
                          style={{ display: "none" }}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) setFormPhoto(file);
                          }}
                        />
                      </div>

                      {formPhoto && (
                        <button
                          className="btn btn-success btn-sm mt-2"
                          onClick={handleSavePhoto}
                        >
                          Save Photo
                        </button>
                      )}
                    </div>

                    {/* Account Info */}
                    <div className="col-sm-8 col-md-7 col-lg-8 mb-3">
                      <AccountInfo
                        account={account}
                        formAccount={formAccount}
                        setFormAccount={setFormAccount}
                        onSave={handleSaveAccount}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right section: Order summary */}
            <div className="col-md-4">
              <h4 className="text-secondary">Order Summary</h4>
              <p>
                <strong>Total Orders:</strong> 12 <br />
                <strong>Pending Orders:</strong> 3 <br />
                <strong>Completed Orders:</strong> 9 <br />
              </p>
              <button className="btn btn-lg btn-outline-primary" onClick={handleAddBusiness}>
                Add More Business
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
